import React, { useEffect, useState } from 'react';
import { Trophy, Sparkles, Loader2, Ban, XCircle } from 'lucide-react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './ui/dialog';
import { Skeleton } from './ui/skeleton';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from './ui/alert-dialog';
import { Textarea } from './ui/textarea';
import { useAuth } from '../context/AuthContext';
import { gamificationAPI } from '../services/api';

const formatDate = (value, withTime = true) => {
  if (!value) return '—';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return '—';
  return new Intl.DateTimeFormat('pt-BR', withTime ? { dateStyle: 'short', timeStyle: 'short' } : { dateStyle: 'long' }).format(parsed);
};

const TYPE_LABELS = {
  avanco_diario: 'Avanço diário',
  conexao_positiva: 'Conexão positiva com outro membro',
  pequena_meta: 'Pequena meta concluída',
  contribuicao_mastermind: 'Contribuição extraordinária para o Mastermind',
  grande_meta: 'Grande meta concluída',
};

const typeLabel = (type) => TYPE_LABELS[type] || 'Não pontuável';

const TypeBadge = ({ type }) => {
  if (type === 'grande_meta') {
    return (
      <Badge className="border border-amber-400/30 bg-amber-950/70 text-amber-200">
        <Trophy size={12} className="mr-1" /> Grande meta concluída
      </Badge>
    );
  }
  if (TYPE_LABELS[type]) {
    return (
      <Badge className="border border-indigo-400/30 bg-indigo-950/70 text-indigo-200">
        <Sparkles size={12} className="mr-1" /> {TYPE_LABELS[type]}
      </Badge>
    );
  }
  return <Badge className="border border-gray-700 bg-gray-900 text-gray-400">Não pontuável</Badge>;
};

const RejectButton = ({ recordId, onRejected }) => {
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleReject = async () => {
    setSubmitting(true);
    try {
      await gamificationAPI.rejectAchievement(recordId, reason.trim() || undefined);
      toast.success('Conquista rejeitada.');
      setReason('');
      onRejected?.();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Não foi possível rejeitar esta conquista.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AlertDialog>
      <AlertDialogTrigger asChild>
        <Button size="sm" variant="outline" className="border-red-500/30 bg-red-500/10 text-red-200 hover:bg-red-500/20">
          <Ban size={14} className="mr-1" /> Rejeitar
        </Button>
      </AlertDialogTrigger>
      <AlertDialogContent className="glass-card border-white/10">
        <AlertDialogHeader>
          <AlertDialogTitle className="text-white">Rejeitar conquista?</AlertDialogTitle>
          <AlertDialogDescription className="text-neutral-400">
            Os pontos desta conquista serão zerados e ela sairá do ranking. O registro continua visível para auditoria.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <div className="space-y-2">
          <label className="text-xs text-neutral-400">Motivo (opcional)</label>
          <Textarea
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            placeholder="Ex.: relato sem sentido / spam"
            className="bg-neutral-900/50 border-neutral-800 text-white text-sm"
          />
        </div>
        <AlertDialogFooter>
          <AlertDialogCancel className="bg-neutral-800 text-white border-neutral-700">Cancelar</AlertDialogCancel>
          <AlertDialogAction onClick={handleReject} disabled={submitting} className="bg-red-600 hover:bg-red-700">
            {submitting ? <Loader2 size={14} className="mr-1 animate-spin" /> : null}
            Rejeitar
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

const GamificationAchievementsModal = ({ open, onOpenChange, userId, userName, month, onRejected }) => {
  const { user: currentUser } = useAuth();
  const isAdminView = currentUser?.role === 'ADMIN';

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const loadAchievements = async () => {
    if (!userId) return;
    setLoading(true);
    setError('');
    try {
      const response = await gamificationAPI.getUserAchievements(userId, month);
      setItems(response.data || []);
    } catch (err) {
      setError(err?.response?.data?.detail || 'Não foi possível carregar as conquistas.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open && userId) {
      loadAchievements();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, userId, month]);

  const handleRejected = () => {
    loadAchievements();
    onRejected?.();
  };

  const visibleItemsForMember = items.filter(
    (item) => (item.points_awarded || 0) > 0 && item.admin_action !== 'rejected'
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="glass-card border-white/10 sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle className="text-white">Conquistas de {userName || 'membro'}</DialogTitle>
          <DialogDescription className="text-slate-300">
            {isAdminView
              ? 'Todos os relatos do mês, incluindo os não pontuáveis e rejeitados.'
              : 'Suas conquistas registradas neste mês.'}
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="space-y-3">
            {[1, 2, 3].map((item) => (
              <Skeleton key={`achievement-skeleton-${item}`} className="h-16 w-full bg-white/10" />
            ))}
          </div>
        ) : error ? (
          <div className="rounded-lg border border-red-500/40 bg-red-500/10 p-4 text-red-200">{error}</div>
        ) : isAdminView ? (
          items.length === 0 ? (
            <div className="rounded-lg border border-white/10 bg-black/20 px-4 py-6 text-center text-slate-300">
              Nenhum relato registrado neste mês.
            </div>
          ) : (
            <div className="space-y-3 max-h-[60vh] overflow-y-auto pr-1">
              {items.map((item) => {
                const isRejected = item.admin_action === 'rejected';
                return (
                  <div key={item.id} className="rounded-lg border border-white/10 bg-gray-800/50 p-4 space-y-2">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <div className="flex items-center gap-2">
                        <TypeBadge type={item.final_type} />
                        {isRejected && (
                          <Badge className="border border-red-500/30 bg-red-950/70 text-red-200">
                            <XCircle size={12} className="mr-1" /> Rejeitada
                          </Badge>
                        )}
                      </div>
                      <span className="text-xs text-slate-400">{formatDate(item.evaluated_at || item.filled_at)}</span>
                    </div>
                    <p className="text-sm text-white">{item.description || '—'}</p>
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <p className="text-xs text-slate-400">
                        Reivindicado como <strong>{typeLabel(item.claimed_type)}</strong> · {item.points_awarded ?? 0} pontos
                      </p>
                      {!isRejected && (item.points_awarded || 0) > 0 && (
                        <RejectButton recordId={item.id} onRejected={handleRejected} />
                      )}
                    </div>
                    {item.reasoning && (
                      <p className="text-xs text-slate-500 italic">"{item.reasoning}"</p>
                    )}
                  </div>
                );
              })}
            </div>
          )
        ) : visibleItemsForMember.length === 0 ? (
          <div className="rounded-lg border border-white/10 bg-black/20 px-4 py-6 text-center text-slate-300">
            Nenhuma conquista pontuada neste mês ainda.
          </div>
        ) : (
          <ul className="space-y-3 max-h-[60vh] overflow-y-auto pr-1">
            {visibleItemsForMember.map((item) => (
              <li key={item.id} className="rounded-lg border border-white/10 bg-black/20 p-4">
                <div className="mb-1 flex flex-wrap items-center gap-2">
                  <TypeBadge type={item.final_type} />
                  <span className="text-xs text-slate-400">{formatDate(item.evaluated_at || item.filled_at, false)}</span>
                </div>
                <p className="text-sm text-slate-200">{item.description}</p>
              </li>
            ))}
          </ul>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default GamificationAchievementsModal;
